import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ThemeProvider, StyledEngineProvider, useTheme, styled, Theme } from '@mui/material/styles';
import logo from './../logo.png';
import { AgencyPicker } from './AgencyPicker';
import { AgencyLogo } from './AgencyLogo';
import { darkTheme } from '../themes';
import { serviceLocator } from '../services/service-locator';

const authenticationService = serviceLocator.getAuthenticationService();
const drawerWidth = 288;

const Root = styled('div')({
  display: 'flex',
  minHeight: '100vh',
  position: 'relative',
  isolation: 'isolate',
});

const Aurora = styled('div')({
  position: 'fixed',
  inset: 0,
  zIndex: -1,
  pointerEvents: 'none',
  backgroundImage:
    'radial-gradient(1200px 700px at 0% -10%, rgba(5, 35, 114, 0.10), transparent 60%),' +
    'radial-gradient(900px 600px at 100% 0%, rgba(250, 135, 28, 0.07), transparent 55%),' +
    'radial-gradient(700px 500px at 50% 110%, rgba(94, 124, 255, 0.08), transparent 60%)',
});

const DrawerNav = styled('nav', { shouldForwardProp: (p) => p !== 'desktopOpen' })<{ desktopOpen: boolean }>(({ theme, desktopOpen }) => ({
  [theme.breakpoints.up('sm')]: {
    width: desktopOpen ? drawerWidth : 0,
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.standard,
    }),
  },
}));

const drawerTransition = (theme: Theme) => theme.transitions.create(['margin', 'width'], {
  easing: theme.transitions.easing.sharp,
  duration: theme.transitions.duration.standard,
});

const StyledAppBar = styled(AppBar, { shouldForwardProp: (p) => p !== 'desktopOpen' })<{ desktopOpen: boolean }>(({ theme, desktopOpen }) => ({
  background:
    'linear-gradient(180deg, rgba(3, 17, 80, 0.92) 0%, rgba(4, 21, 79, 0.86) 100%)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
  boxShadow: '0 1px 0 rgba(255,255,255,0.04) inset, 0 12px 32px -16px rgba(2, 10, 48, 0.6)',
  transition: drawerTransition(theme),
  // A thin orange ignition line under the bar.
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: -1,
    height: 1,
    background:
      'linear-gradient(90deg, transparent 0%, rgba(250, 135, 28, 0.0) 12%, rgba(250, 135, 28, 0.55) 50%, rgba(250, 135, 28, 0.0) 88%, transparent 100%)',
    pointerEvents: 'none',
  },
  [theme.breakpoints.up('sm')]: {
    marginLeft: desktopOpen ? drawerWidth : 0,
    width: desktopOpen ? `calc(100% - ${drawerWidth}px)` : '100%',
  },
}));

const MenuButton = styled(IconButton)({
  marginRight: 12,
});

const ToolbarSpacer = styled('div')(({ theme }) => ({
  ...theme.mixins.toolbar,
}));

const Content = styled('main', { shouldForwardProp: (p) => p !== 'desktopOpen' })<{ desktopOpen: boolean }>(({ theme, desktopOpen }) => ({
  flexGrow: 1,
  position: 'relative',
  // Transparent so the Aurora layer shows through; surfaces inside still
  // get their own opaque background as needed.
  backgroundColor: 'transparent',
  transition: drawerTransition(theme),
  [theme.breakpoints.up('sm')]: {
    maxWidth: desktopOpen ? `calc(100% - ${drawerWidth}px)` : '100%',
  },
}));

const drawerPaperSx = {
  width: drawerWidth,
  border: 'none',
  backgroundImage: 'none',
  background:
    'linear-gradient(180deg, #03124a 0%, #04154f 32%, #051a5e 100%)',
  color: 'rgba(244, 246, 255, 0.86)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    backgroundImage:
      'radial-gradient(420px 280px at 8% -8%, rgba(250, 135, 28, 0.20), transparent 55%),' +
      'radial-gradient(360px 260px at 100% 0%, rgba(94, 124, 255, 0.22), transparent 55%),' +
      'radial-gradient(360px 320px at 50% 105%, rgba(5, 35, 114, 0.45), transparent 60%)',
    opacity: 0.95,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: 1,
    height: '100%',
    background:
      'linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.02) 50%, rgba(255,255,255,0) 100%)',
    pointerEvents: 'none',
  },
};

const LogoHeader = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  height: 88,
  paddingInline: theme.spacing(3),
  borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
  zIndex: 1,
}));

const LogoMark = styled('img')({
  width: '100%',
  height: 'auto',
  maxHeight: 64,
  display: 'block',
  objectFit: 'contain',
  filter: 'brightness(0) invert(1)',
  opacity: 0.96,
});

const NavScroll = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  position: 'relative',
  zIndex: 1,
  // React Router's <Link> renders an <a>; without explicit :link/:visited
  // rules the browser's visited-link color (a saturated purple/blue) outranks
  // the MUI palette and the selected text appears to "blend into" the dark
  // sidebar. Pin every link state to the design palette.
  '& a, & a:link, & a:visited, & a:hover, & a:active, & a:focus': {
    textDecoration: 'none',
    color: 'inherit',
  },
  '& .MuiListItemButton-root': {
    color: 'rgba(244, 246, 255, 0.78)',
    transition: 'background-color 200ms ease, color 200ms ease',
  },
  '& .MuiListItemButton-root:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    color: '#ffffff',
  },
  '& .MuiListItemButton-root.Mui-selected, & .MuiListItemButton-root.Mui-selected:hover': {
    backgroundColor: 'rgba(250, 135, 28, 0.18)',
    color: '#ffffff',
  },
  '& .MuiListItemButton-root.Mui-selected .MuiListItemText-primary': {
    color: '#ffffff',
    fontWeight: 600,
  },
  '& .MuiListItemButton-root.Mui-selected .MuiListItemIcon-root': {
    color: '#ffb45c',
  },
  '& .MuiListItemButton-root.Mui-selected::before': {
    content: '""',
    position: 'absolute',
    left: -10,
    top: 8,
    bottom: 8,
    width: 3,
    borderRadius: 3,
    background: 'linear-gradient(180deg, #ffb45c 0%, #FA871C 100%)',
    boxShadow: '0 0 14px rgba(250, 135, 28, 0.55)',
  },
  '& .MuiListItemText-primary': {
    color: 'inherit',
  },
}));

const SectionLabel = styled('div')(({ theme }) => ({
  fontFamily: '"JetBrains Mono", "SFMono-Regular", Menlo, monospace',
  fontSize: 10.5,
  fontWeight: 500,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'rgba(244, 246, 255, 0.42)',
  paddingInline: theme.spacing(3),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(0.75),
}));

const DrawerFooter = styled('div')(({ theme }) => ({
  position: 'relative',
  marginTop: 'auto',
  padding: theme.spacing(1.75, 2.5),
  borderTop: '1px solid rgba(255, 255, 255, 0.08)',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  zIndex: 1,
  background:
    'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(2, 10, 48, 0.4) 100%)',
}));

const StatusDot = styled('span')({
  width: 8,
  height: 8,
  borderRadius: '50%',
  background: 'linear-gradient(180deg, #6ce39a 0%, #2fb673 100%)',
  boxShadow: '0 0 0 3px rgba(108, 227, 154, 0.18)',
  flexShrink: 0,
});

interface ResponsiveDrawerProps {
  theme: Theme;
  listItems: any;
  content: any;
  isSmUp: boolean;
}

interface ResponsiveDrawerState {
  mobileOpen: boolean;
  desktopOpen: boolean;
}

class ResponsiveDrawerInner extends React.Component<ResponsiveDrawerProps, ResponsiveDrawerState> {
  private mounted: boolean = false;

  constructor(props: ResponsiveDrawerProps) {
    super(props);
    this.state = {
      mobileOpen: false,
      desktopOpen: true,
    };
  }

  public componentDidMount() {
    this.mounted = true;
  }

  public componentWillUnmount() {
    this.mounted = false;
  }

  public render(): any {
    const { content, listItems, theme, isSmUp } = this.props;

    const authUserData = authenticationService.getAuthenticatedUserData();
    const hasAgency = authUserData?.ownedAgency != null;
    const username = authUserData?.username;

    const drawer = (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
        <LogoHeader>
          <LogoMark src={logo} alt="WebDrvn" />
        </LogoHeader>
        <SectionLabel>Navigation</SectionLabel>
        <NavScroll>
          <List disablePadding>
            {listItems}
          </List>
        </NavScroll>
        {username && (
          <DrawerFooter>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '12px',
                background:
                  'linear-gradient(135deg, rgba(250, 135, 28, 0.92) 0%, rgba(255, 180, 92, 0.92) 100%)',
                color: '#1a0f00',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 700,
                flexShrink: 0,
                boxShadow:
                  '0 1px 0 rgba(255,255,255,0.32) inset, 0 8px 18px -8px rgba(250, 135, 28, 0.55)',
              }}
              aria-hidden
            >
              {username.charAt(0).toUpperCase()}
            </Box>
            <Box sx={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <StatusDot aria-hidden />
                <Typography
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: 10,
                    fontWeight: 500,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: 'rgba(244, 246, 255, 0.58)',
                    lineHeight: 1.2,
                  }}
                >
                  Signed in
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.25,
                  color: '#ffffff',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: 14,
                  letterSpacing: '-0.005em',
                }}
              >
                {username}
              </Typography>
            </Box>
          </DrawerFooter>
        )}
      </Box>
    );

    const desktopOpen = isSmUp ? this.state.desktopOpen : true;
    return (
      <Root>
        <CssBaseline />
        <Aurora aria-hidden />
        <StyledAppBar position="fixed" desktopOpen={desktopOpen}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={darkTheme}>
              <Toolbar sx={{ minHeight: { xs: 60, sm: 68 }, paddingInline: { xs: 2, sm: 3 }, gap: 1.5 }}>
                <MenuButton
                  color="inherit"
                  aria-label={isSmUp
                    ? (this.state.desktopOpen ? 'Collapse navigation' : 'Expand navigation')
                    : 'Open drawer'}
                  onClick={this.handleDrawerToggle}
                  size="large">
                  <MenuIcon />
                </MenuButton>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    noWrap
                    sx={{
                      color: '#ffffff',
                      fontFamily: '"Inter Tight", "Inter", sans-serif',
                      fontWeight: 700,
                      letterSpacing: '-0.012em',
                      fontSize: { xs: 16, sm: 17 },
                    }}>
                    WebDrvn
                  </Typography>
                  <Typography
                    noWrap
                    sx={{
                      display: { xs: 'none', md: 'inline-flex' },
                      alignItems: 'center',
                      gap: 1,
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: 11,
                      fontWeight: 500,
                      letterSpacing: '0.16em',
                      textTransform: 'uppercase',
                      color: 'rgba(244, 246, 255, 0.55)',
                    }}>
                    <Box
                      component="span"
                      aria-hidden
                      sx={{
                        display: 'inline-block',
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: '#FA871C',
                        boxShadow: '0 0 0 3px rgba(250, 135, 28, 0.18)',
                      }}
                    />
                    Static Website Manager
                  </Typography>
                </Box>
                <AgencyPicker />
                {hasAgency && (
                  <Box sx={{ marginLeft: 1, display: 'inline-flex', alignItems: 'center' }}>
                    <AgencyLogo avatar={true} />
                  </Box>
                )}
              </Toolbar>
            </ThemeProvider>
          </StyledEngineProvider>
        </StyledAppBar>
        <DrawerNav desktopOpen={desktopOpen}>
          {!isSmUp && (
            <Drawer
              variant="temporary"
              anchor={theme.direction === 'rtl' ? 'right' : 'left'}
              open={this.state.mobileOpen}
              onClick={this.handleDrawerClick}
              id="mobile-menu"
              onClose={this.handleDrawerToggle}
              slotProps={{
                paper: { sx: drawerPaperSx },
                root: { keepMounted: true },
              }}
            >
              {drawer}
            </Drawer>
          )}
          {isSmUp && (
            <Drawer
              slotProps={{
                paper: { sx: drawerPaperSx },
              }}
              variant="persistent"
              anchor="left"
              open={this.state.desktopOpen}
            >
              {drawer}
            </Drawer>
          )}
        </DrawerNav>
        <Content desktopOpen={desktopOpen}>
          <ToolbarSpacer />
          {content}
        </Content>
      </Root>
    );
  }

  private handleDrawerToggle = () => {
    if (this.props.isSmUp) {
      this.setState((state: any) => ({ desktopOpen: !state.desktopOpen }));
    } else {
      this.setState((state: any) => ({ mobileOpen: !state.mobileOpen }));
    }
  };

  private handleDrawerClick = () => {
    if (this.state.mobileOpen) {
      setTimeout(() => this.mounted && this.setState({ mobileOpen: false }), 300);
    }
  };
}

export default function ResponsiveDrawer(props: Omit<ResponsiveDrawerProps, 'theme' | 'isSmUp'>) {
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  return <ResponsiveDrawerInner {...props} theme={theme} isSmUp={isSmUp} />;
}
